// Number, money, and date formatters. Narrow-no-break-space ( ) thousands match the
// design's `ts` helper. Tabular numerics live in tokens.css via .num/.mono.

// U+202F NARROW NO-BREAK SPACE — typographically identical to a thin
// space but non-breaking, so "15 820" never wraps mid-number in narrow
// columns (e.g. the 52px NavRail counts). U+2009 is breakable and caused
// the rail to wrap "AGY / 1 134" onto two lines.
const THIN = ' ';

export function ts(n: number | string | null | undefined, decimals = 0): string {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? Number(n) : n;
  if (Number.isNaN(num)) return '—';
  const fixed = num.toFixed(decimals);
  const [int, dec] = fixed.split('.');
  const grouped = (int ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, THIN);
  return dec ? `${grouped}.${dec}` : grouped;
}

// ISO-4217 → display symbol. Codes not listed fall back to a "CODE " prefix
// (e.g. "PLN 2.3M") so an unmapped currency is still unambiguous, never wrong.
const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  CAD: 'C$',
  AUD: 'A$',
  NZD: 'NZ$',
  GBP: '£',
  EUR: '€',
  JPY: '¥',
  CHF: 'CHF ',
};

function currencyPrefix(currency: string | null | undefined): string {
  if (!currency) return '$';
  const code = currency.trim().toUpperCase();
  return CURRENCY_SYMBOL[code] ?? `${code} `;
}

// Currency-aware compact money: £2.3M, €450K, C$1.2B, $980. The magnitude
// suffix (K/M/B/T) is currency-agnostic; only the prefix changes. Amounts are
// shown in their native currency — we do NOT convert (no FX rate in the hot
// path, and a converted number would silently drift).
export function compactMoney(
  n: number | string | null | undefined,
  currency?: string | null,
): string {
  if (n == null || n === '') return '—';
  // Postgres numeric columns arrive as strings via the pg driver — coerce.
  const num = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(num)) return '—';
  const p = currencyPrefix(currency);
  const abs = Math.abs(num);
  if (abs >= 1e12) return `${p}${(num / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${p}${(num / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${p}${(num / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${p}${(num / 1e3).toFixed(1)}K`;
  return `${p}${num.toFixed(0)}`;
}

// Back-compat alias: USD-only compact money. Prefer compactMoney(n, currency).
export function compactUsd(n: number | string | null | undefined): string {
  return compactMoney(n, 'USD');
}

function relDays(target: string | Date | null | undefined): number | null {
  if (!target) return null;
  const t = typeof target === 'string' ? new Date(target) : target;
  if (Number.isNaN(t.getTime())) return null;
  const diffMs = t.getTime() - Date.now();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

export function relTime(target: string | Date | null | undefined): string {
  const days = relDays(target);
  if (days == null) return '—';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  const abs = Math.abs(days);
  // Sabre W3-fix: bucket older dates into months/years so "7840d ago" reads as
  // "21y ago". Boundaries: 1-30d → days, 31-364d → months (30d/mo), 365d+ →
  // years (365d/yr). Future and past share the same magnitude bucketing; only
  // the suffix differs.
  let unit: string;
  if (abs <= 30) {
    unit = `${abs}d`;
  } else if (abs < 365) {
    unit = `${Math.round(abs / 30)}mo`;
  } else {
    unit = `${Math.round(abs / 365)}y`;
  }
  return days > 0 ? unit : `${unit} ago`;
}

export function dateOnly(s: string | null | undefined): string {
  if (!s) return '—';
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export function timeShort(s: string | Date | null | undefined): string {
  if (!s) return '—';
  const d = typeof s === 'string' ? new Date(s) : s;
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}
